<i18n>
{
  "ja": {
    "hd2" : "セミナー内容",
    "success":[
      ":ありがとうございます！: :情報の送信が完了しました。:",
      ":メールボックスを::ご確認ください。:"
    ],
    "submitted_title": "セミナー申し込み完了"
  }
}
</i18n>

<template>
<div class="overflow-hidden">
  <div class="max-w-6xl lg:w-7/8 lg:mx-auto lg:flex lg:my-0">
    <div class="w-7/8 mx-auto lg:w-8/12 lg:flex-shrink-0 lg:pr-12 pt-16 lg:pb-24">
      <div v-if="article">
        <p class="text-center lg:text-left text-xs md:text-sm">{{ article.seminar_category.name }}</p>
        <h1 class="mt-3 text-center lg:text-left text-2xl md:text-4xl leading-tight font-semibold whitespace-pre-wrap">{{ article.title }}</h1>
        <p v-if="article.time" class="flex items-center justify-center lg:justify-start text-xs mt-4">
          <img src="@/assets/img/icon_clock.svg" width="18" alt="" class="mr-2">
          {{ article.time }} min
        </p>
        <p v-if="article.location" class="flex items-center justify-center lg:justify-start text-xs mt-2">
          <img src="@/assets/img/icon_pin.svg" width="14" alt="" class="mr-2">
          {{ article.location }}
        </p>
        <div v-if="article.thumbnail"
          :style="{ backgroundImage: `url(${$options.filters.cmsURL(article.thumbnail.url)})` }"
          class="mt-6 rounded w-full bg-contain bg-center bg-no-repeat thumbnail"
        />
        <div v-if="article.content" class="content">
          <h2 class="mt-6">{{ $t("hd2") }} </h2>
          <div v-html="$md.render(article.content)" />
        </div>
      </div>
    </div>
    <div :id="`form-${formId}`" class="form mt-16 lg:mt-0 lg:w-4/12 lg:flex-shrink-0 lg:px-12 pt-16 pb-24 relative">
      <div class="absolute left-0 inset-y-0 w-screen bg-primary"></div>
      <div class="relative w-7/8 mx-auto lg:w-auto lg:ml-0 lg:-mr-20 xl:-mr-24 xxl:-mr-32 text-black">
        <client-only>
          <div v-if="isLoading">
            <p>Loading...</p>
          </div>
          <hs-form v-else-if="formId && portalId" @submit="onSubmit" :form-id="formId" :portal-id="portalId">
            <template v-slot:success>
              <div class="success text-center max-w-md mx-auto px-8">
                <p class="text-lg leading-loose my-8" v-for=" (text, index) in $t('success') " :key="index" v-html="$md.render(text)" />
              </div>
            </template>
          </hs-form>
          <div v-else>
            <div v-if="error">
              {{ error }}
            </div>
            <div v-else>
              Server Error
            </div>
          </div>
        </client-only>
      </div>
    </div>
  </div>
  <BreadcrumbsFoot :list="breadcrumbs" />
</div>
</template>

<style lang="sass" scoped>
@import '~assets/css/cms-forms.sass'
.thumbnail
  aspect-ratio: 100/56
</style>

<script>
import hsForm from "@/components/hsForm";
import urljoin from "url-join";
import BreadcrumbsFoot from '@/components/breadcrumbsFoot';
import Jsonld from '@/components/mixins/jsonld';

export default {
  mixins: [Jsonld],
  components: {
    hsForm,
    BreadcrumbsFoot
  },
  data() {
    return {
      isSubmitted: false,
      loading: { portalId: false, formId: false },
      error: false,
      portalId: null
    }
  },
  async middleware({ $strapi, params, redirect }) {
    if(Number.isInteger(Number(params.slug))) {
      const articles = await $strapi.$http.$get(`seminars?id=${params.slug}`);
      if(articles.length > 0) {
        return redirect(`/seminar/${articles[0].slug}`)
      }
    };
  },
  async asyncData({ $strapi, params, error, app }) {
    let article, formId
    try {
      article = await $strapi.$http.$get(`seminars?slug=${params.slug}`);
      article = article[0] || null;
      formId = article && article.formId

      if (!article) {
        throw new Error('Not found')
      }
    } catch (e) {
      error({
        statusCode: 404,
        message: e.message
      })
    }
    return {
      article,
      formId,
      breadcrumbs: [
        { name: app.i18n.t('nav.seminar'), url: app.localePath('/seminar/') },
        { name: article.title } 
      ],
    };
  },
  created () {
    this.loading["portalId"] = true;
    this.$normalizeConfig("portalId")
      .then((pid) => { this.portalId = pid; })
      .finally(() => { this.loading["portalId"] = false });
  },
  methods: {
    onSubmit() {
      this.$gtm.push({
        trackPageview: urljoin(this.$route.fullPath, 'thankyou'),
        title: this.item && this.item.title ? this.item.title + " " : "" + this.$t('submitted_title'),
        event: 'loadready',
      });
      this.$hsq.pageView(urljoin(this.$route.fullPath, 'thankyou'));
      this.isSubmitted = true;
    }
  },
  watch: {
    isSubmitted(v) {
      if(v) {
        const form = document.getElementById(`form-${this.formId}`)
        const rect = form.getBoundingClientRect()
        const offsetTop = window.pageYOffset + rect.top - 120
        window.scrollTo({left:0, top:offsetTop, behavior: 'smooth'})
      }
    }
  },
  computed: {
    isLoading () {
      return Object.values(this.loading).includes(true);
    },
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    }
  },
  head() {
    const { article } = this;
    const { seo } = this.homepage;
    const { defaultSeo } = this.global;
    const fullSeo = {
      ...defaultSeo,
      ...seo,
      metaTitle: article.title.replace(/\r?\n/g,''),
      metaDescription: article.description,
      metaKeywords: `${this.$t('nav.seminar')},${this.$t('keywords')}`
    };
    if (article.thumbnail) {
      fullSeo.shareImage = article.thumbnail
    }
    return {
      title: fullSeo.metaTitle,
      meta: this.$options.filters.cmsMetaTags(fullSeo)
    };
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  },
};
</script>